import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Divider,
  IconButton,
  Paper,
  CardMedia,
  Chip,
  CardHeader,
  Tooltip,
  Tab,
  Tabs,
  Link,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  Pagination,
  Fade,
  alpha,
  Skeleton,
  useMediaQuery,
  AlertTitle,
  Alert
} from '@mui/material';
import { Reply, ThumbUp, Delete, Edit, Image as ImageIcon, AttachFile, Add, Reply as ReplyIcon, Close as CloseIcon, ExpandMore as ExpandMoreIcon, Comment as CommentIcon, Settings, ForumOutlined, FilterList } from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import axiosInstance from '../axiosInstance';
import { formatDistanceToNow } from 'date-fns';
import { useTheme } from '@mui/material/styles';
import { Post, Category, User, Comment, PaginatedResponse } from './types/forum.types';
import { CategoryFilter } from './components/CategoryFilter';
import { PostCard } from './components/PostCard';
import { NewPostDialog } from './components/NewPostDialog';
import { CategoryManagement } from './components/CategoryManagement';
import { useUser } from '../../Components/UserContext';

const Forum: React.FC = () => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [totalPosts, setTotalPosts] = useState(0);
  const [categories, setCategories] = useState<Category[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [comments, setComments] = useState<{ [postId: string]: Comment[] }>({});
  const [loadedImages, setLoadedImages] = useState<{ [key: string]: string }>({});
  
  // UI state
  const [page, setPage] = useState(0);
  const [limit] = useState(5);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [expandedPost, setExpandedPost] = useState<string | null>(null);
  
  // Dialog states
  const [newPostOpen, setNewPostOpen] = useState(false);
  
  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingPostId, setEditingPostId] = useState<string | null>(null);
  
  // Form states
  const [newPostTitle, setNewPostTitle] = useState('');
  const [newPostContent, setNewPostContent] = useState('');
  const [newPostTags, setNewPostTags] = useState<string>('');
  const [selectedPostCategory, setSelectedPostCategory] = useState<number | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isCategoryManagementOpen, setIsCategoryManagementOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filterVisible, setFilterVisible] = useState(true);

  // Add new state for deletion confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [postToDelete, setPostToDelete] = useState<Post | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteSuccess, setDeleteSuccess] = useState<boolean>(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDarkTheme = theme.name === 'dark';

  // Use UserContext to get user info
  const { user: contextUser } = useUser();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [categoriesResponse, usersResponse] = await Promise.all([
        axiosInstance.get('/forum/categories/'),
        axiosInstance.get('/auth/users')
      ]);

      const postsUrl = selectedCategory 
        ? `/forum/categories/${selectedCategory}/posts/?skip=${page * limit}&limit=${limit}`
        : `/forum/posts/all?skip=${page * limit}&limit=${limit}`;
      
      let currentPosts: Post[] = [];
      try {
        const postsResponse = await axiosInstance.get(postsUrl);
        const paginatedData = postsResponse.data as PaginatedResponse;
        setPosts(paginatedData.posts);
        setTotalPosts(paginatedData.total);
        currentPosts = paginatedData.posts;
      } catch (error: any) {
        if (error.response?.status === 404 && error.response?.data?.detail === "No posts found in this category") {
          setPosts([]);
          setTotalPosts(0);
          currentPosts = [];
        } else {
          console.error('Error fetching posts:', error);
        }
      }

      setCategories(categoriesResponse.data);
      setUsers(usersResponse.data);

      // Load files for all posts
      currentPosts.forEach(post => {
        post.files.forEach(async file => {
          try {
            const response = await axiosInstance.get(`/forum/uploads/${post.id}/${file.filename}`, {
              responseType: 'blob'
            });
            const imageUrl = URL.createObjectURL(response.data);
            setLoadedImages(prev => ({
              ...prev,
              [`${post.id}_${file.filename}`]: imageUrl
            }));
          } catch (error) {
            console.error(`Error loading file ${file.filename}:`, error);
          }
        });
      });
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, page, limit]);

  useEffect(() => {
    fetchData();

    return () => {
      Object.values(loadedImages).forEach(url => {
        URL.revokeObjectURL(url);
      });
    };
  }, [fetchData]);

  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const role = localStorage.getItem('role');
        
        // If we have contextUser, use its ID, otherwise try localStorage
        const userId = contextUser?.user_id || Number(localStorage.getItem('user_id')) || undefined;
        
        setCurrentUser({ 
          role, 
          user_id: userId
        } as User);
      } catch (error) {
        console.error('Error getting user info:', error);
      }
    };

    fetchCurrentUser();
  }, [contextUser]);

  const handleNewPost = async () => {
    if (!selectedPostCategory) return;

    try {
      const formData = new FormData();
      formData.append('title', newPostTitle);
      formData.append('content', newPostContent);
      formData.append('category_id', selectedPostCategory.toString());
      
      if (newPostTags.trim()) {
        formData.append('tags', newPostTags.trim());
      }

      if (selectedFile) {
        formData.append('file', selectedFile);
      }

      const response = await axiosInstance.post('/forum/posts/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      setPosts(prevPosts => [response.data, ...prevPosts]);
      handleCloseNewPost();
      return response.data;
    } catch (error) {
      console.error('Error creating post:', error);
      throw error;
    }
  };

  const handleEditPost = (post: Post) => {
    setIsEditMode(true);
    setEditingPostId(post.id);
    setNewPostTitle(post.title);
    setNewPostContent(post.content);
    setSelectedPostCategory(post.category_id);
    setNewPostTags(post.tags ? post.tags.join(', ') : '');
    setNewPostOpen(true);
  };

  const handleUpdatePost = async () => {
    if (!editingPostId || !selectedPostCategory) return;

    try {
      const updateData: any = {
        title: newPostTitle,
        content: newPostContent,
        category_id: selectedPostCategory,
      };

      if (newPostTags.trim()) {
        updateData.tags = newPostTags.split(',').map(tag => tag.trim());
      }

      const response = await axiosInstance.patch(
        `/forum/posts/${editingPostId}`, 
        updateData
      );
      
      // First update the post in state for immediate feedback
      setPosts(prevPosts => 
        prevPosts.map(post => 
          post.id === editingPostId ? response.data : post
        )
      );
      
      // Close the dialog
      handleCloseNewPost();
      
      // Then refetch all posts to ensure everything is up to date
      await fetchData();
      
      return response.data;
    } catch (error) {
      console.error('Error updating post:', error);
      throw error;
    }
  };

  const handleCloseNewPost = () => {
    setNewPostOpen(false);
    setNewPostTitle('');
    setNewPostContent('');
    setSelectedPostCategory(null);
    setSelectedFile(null);
    setNewPostTags('');
    setIsEditMode(false);
    setEditingPostId(null);
  };

  const handleExpandThread = async (postId: string, forceRefresh = false) => {
    // Toggle behavior: if already expanded and the same post, collapse it
    if (expandedPost === postId && !forceRefresh) {
      setExpandedPost(null);
      return;
    }

    // Set this post as expanded
    setExpandedPost(postId);
    
    try {
      const response = await axiosInstance.get(`/forum/posts/${postId}/comments/`);
      // Sort comments - newest first based on created_at timestamp
      const sortedComments = response.data.sort((a, b) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      
      setComments(prev => ({
        ...prev,
        [postId]: sortedComments
      }));
      
      // Update comment count in the post
      setPosts(prev => prev.map(post => {
        if (post.id === postId) {
          return {
            ...post,
            comments_count: response.data.length
          };
        }
        return post;
      }));
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const getCategoryName = (categoryId: number) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category?.name || 'Uncategorized';
  };

  const getUserInfo = (authorId: number) => {
    return users.find(user => user.user_id === authorId);
  };

  const refreshPosts = async () => {
    await fetchData();
  };

  const toggleFilters = () => {
    setFilterVisible(!filterVisible);
  };

  const handleDeletePost = (post: Post) => {
    setPostToDelete(post);
    setDeleteDialogOpen(true);
    setDeleteError(null);
    setDeleteSuccess(false);
  };

  const confirmDeletePost = async () => {
    if (!postToDelete) return;
    
    try {
      await axiosInstance.delete(`/forum/posts/${postToDelete.id}`);
      
      // Close dialog
      setDeleteDialogOpen(false);
      
      // Show success message briefly
      setDeleteSuccess(true);
      setTimeout(() => setDeleteSuccess(false), 3000);
      
      // Remove post from state (for immediate UI feedback)
      setPosts(prevPosts => prevPosts.filter(post => post.id !== postToDelete.id));
      
      // Reset postToDelete
      setPostToDelete(null);
      
      // Refetch posts to ensure everything is up to date
      await fetchData();
    } catch (error: any) {
      console.error('Error deleting post:', error);
      setDeleteError(
        error.response?.data?.detail || 
        error.message || 
        'An error occurred while deleting the post'
      );
    }
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setPostToDelete(null);
    setDeleteError(null);
  };

  return (
    <Box 
      sx={{ 
        p: { xs: 1, md: 3 }, 
        maxWidth: 1200, 
        margin: '0 auto',
        transition: 'all 0.3s ease-in-out',
        minHeight: '100vh'
      }}
    >
      {/* Success Alert */}
      {deleteSuccess && (
        <Alert 
          severity="success" 
          sx={{ 
            position: 'fixed', 
            top: 16, 
            right: 16, 
            zIndex: 2000,
            borderRadius: isDarkTheme ? '8px' : '0',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            maxWidth: '400px',
            animation: 'fadeIn 0.3s ease-in-out',
            '@keyframes fadeIn': {
              '0%': { opacity: 0, transform: 'translateY(-20px)' },
              '100%': { opacity: 1, transform: 'translateY(0)' }
            }
          }}
          onClose={() => setDeleteSuccess(false)}
        >
          <AlertTitle>Success</AlertTitle>
          Post deleted successfully
        </Alert>
      )}
      
      {/* Header */}
      <Paper 
        elevation={3} 
        sx={{ 
          p: 3, 
          mb: 3, 
          background: isDarkTheme ? alpha(theme.palette.background.paper, 0.6) : theme.palette.background.paper,
          borderRadius: '8px',
          backdropFilter: 'blur(10px)',
          borderLeft: '4px solid',
          borderColor: 'primary.main'
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 0 } }}>
            <ForumOutlined sx={{ fontSize: 32, mr: 2, color: theme.palette.primary.main }} />
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>Community Forum</Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}>
            {currentUser?.role === 'admin' && (
              <Button
                variant="outlined"
                onClick={() => setIsCategoryManagementOpen(true)}
                startIcon={<Settings />}
                sx={{ 
                  borderRadius: isDarkTheme ? '4px' : '0px',
                  textTransform: 'none',
                  transition: 'all 0.2s ease'
                }}
              >
                Manage Categories
              </Button>
            )}
            <Button
              variant="contained"
              onClick={() => setNewPostOpen(true)}
              startIcon={<Add />}
              sx={{ 
                borderRadius: isDarkTheme ? '4px' : '0px',
                textTransform: 'none',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.2s ease'
              }}
            >
              New Post
            </Button>
            <IconButton 
              onClick={toggleFilters}
              sx={{ 
                bgcolor: filterVisible ? (isDarkTheme ? alpha('#64AFE3', 0.2) : alpha(theme.palette.primary.main, 0.1)) : 'transparent',
                color: isDarkTheme ? '#64AFE3' : 'primary.main',
                borderRadius: '4px',
                border: '1px solid',
                borderColor: isDarkTheme ? '#64AFE3' : 'primary.main',
                '&:hover': {
                  bgcolor: filterVisible 
                    ? (isDarkTheme ? alpha('#64AFE3', 0.3) : alpha(theme.palette.primary.main, 0.2))
                    : (isDarkTheme ? alpha('#64AFE3', 0.1) : alpha(theme.palette.primary.main, 0.05))
                }
              }}
            >
              <FilterList />
            </IconButton>
          </Box>
        </Box>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Join the conversation with other users. Share ideas, ask questions, and collaborate.
        </Typography>
        
        <Collapse in={filterVisible} timeout={300}>
          <CategoryFilter
            categories={categories}
            selectedCategory={selectedCategory}
            onCategorySelect={(categoryId) => {
              setSelectedCategory(categoryId);
              setPage(0);
            }}
          />
        </Collapse>
      </Paper>

      {/* Delete Post Dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={closeDeleteDialog}
        PaperProps={{
          sx: { 
            borderRadius: isDarkTheme ? '8px' : '0px',
            background: isDarkTheme ? theme.palette.background.paper : theme.palette.background.paper,
            p: 1
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Delete sx={{ mr: 1, color: 'error.main' }} />
            Confirm Deletion
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2, minWidth: { xs: '280px', sm: '400px' } }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete this post?
          </Typography>
          {postToDelete && (
            <Paper 
              elevation={0} 
              sx={{ 
                p: 2, 
                bgcolor: isDarkTheme ? alpha(theme.palette.background.secondary, 0.3) : alpha(theme.palette.background.secondary, 0.2),
                borderRadius: isDarkTheme ? '8px' : '0',
                mb: 2
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                {postToDelete.title}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                {postToDelete.content.length > 100 
                  ? `${postToDelete.content.substring(0, 100)}...` 
                  : postToDelete.content}
              </Typography>
            </Paper>
          )}
          
          {deleteError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {deleteError}
            </Alert>
          )}
          
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            This action cannot be undone. All comments and files associated with this post will also be deleted.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={closeDeleteDialog}
            variant="outlined"
            sx={{ borderRadius: isDarkTheme ? '4px' : '0px' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmDeletePost}
            variant="contained"
            color="error"
            sx={{ borderRadius: isDarkTheme ? '4px' : '0px' }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Category Management Dialog */}
      <Dialog 
        open={isCategoryManagementOpen} 
        onClose={() => setIsCategoryManagementOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: isDarkTheme ? '8px' : '0px',
            background: isDarkTheme ? theme.palette.background.paper : theme.palette.background.paper,
            backdropFilter: 'blur(10px)'
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Settings sx={{ mr: 1, color: theme.palette.primary.main }} />
            Category Management
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <CategoryManagement 
            categories={categories}
            onCategoryChange={() => {
              axiosInstance.get('/forum/categories/').then(response => {
                setCategories(response.data);
              });
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setIsCategoryManagementOpen(false)}
            variant="outlined"
            sx={{ borderRadius: isDarkTheme ? '4px' : '0px' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Post List */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {loading ? (
          // Loading skeletons
          Array.from(new Array(3)).map((_, index) => (
            <Paper 
              key={index} 
              elevation={3}
              sx={{ 
                p: 2, 
                borderRadius: '8px',
                background: isDarkTheme ? alpha(theme.palette.background.paper, 0.6) : theme.palette.background.paper
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
                <Box sx={{ width: '100%' }}>
                  <Skeleton variant="text" width="60%" height={30} />
                  <Skeleton variant="text" width="30%" height={20} />
                </Box>
              </Box>
              <Skeleton variant="text" width="90%" />
              <Skeleton variant="text" width="95%" />
              <Skeleton variant="text" width="80%" />
              <Skeleton variant="rectangular" width="100%" height={100} sx={{ mt: 2, borderRadius: '4px' }} />
            </Paper>
          ))
        ) : posts.length === 0 ? (
          <Fade in={true} timeout={500}>
            <Paper 
              elevation={3}
              sx={{ 
                p: 4, 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '8px',
                background: isDarkTheme ? alpha(theme.palette.background.paper, 0.6) : theme.palette.background.paper,
                borderLeft: '4px solid',
                borderColor: 'primary.main',
                minHeight: '200px'
              }}
            >
              <CommentIcon sx={{ fontSize: 60, color: alpha(theme.palette.text.primary, 0.2), mb: 2 }} />
              <Typography variant="h6" color="text.secondary">No posts found</Typography>
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1, mb: 3 }}>
                {selectedCategory ? 'There are no posts in this category yet.' : 'There are no posts yet.'}
              </Typography>
              <Button 
                variant="contained" 
                startIcon={<Add />} 
                onClick={() => setNewPostOpen(true)}
                sx={{ 
                  borderRadius: isDarkTheme ? '4px' : '0px',
                  textTransform: 'none'
                }}
              >
                Create the first post
              </Button>
            </Paper>
          </Fade>
        ) : (
          posts.map(post => (
            <Fade key={post.id} in={true} timeout={500}>
              <Box>
                <PostCard
                  initialPost={post}
                  userInfo={getUserInfo(post.author_id)}
                  categoryName={getCategoryName(post.category_id)}
                  comments={comments[post.id] || []}
                  isExpanded={expandedPost === post.id}
                  loadedImages={loadedImages}
                  onExpandThread={handleExpandThread}
                  allUsers={users}
                  currentUserId={currentUser?.user_id}
                  onEditPost={handleEditPost}
                  onDeletePost={handleDeletePost}
                />
              </Box>
            </Fade>
          ))
        )}
      </Box>

      {/* New/Edit Post Dialog */}
      <NewPostDialog
        open={newPostOpen}
        categories={categories}
        onClose={handleCloseNewPost}
        onSubmit={isEditMode ? handleUpdatePost : handleNewPost}
        title={newPostTitle}
        content={newPostContent}
        tags={newPostTags}
        selectedCategory={selectedPostCategory}
        selectedFile={selectedFile}
        onTitleChange={setNewPostTitle}
        onContentChange={setNewPostContent}
        onTagsChange={setNewPostTags}
        onCategoryChange={setSelectedPostCategory}
        onFileChange={setSelectedFile}
        isEditMode={isEditMode}
      />

      {/* Pagination */}
      {totalPosts > 0 && (
        <Box sx={{ 
          position: 'sticky',
          bottom: 0,
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          py: 2,
          mt: 3,
          bgcolor: isDarkTheme ? alpha(theme.palette.background.default, 0.8) : theme.palette.background.default,
          borderTop: '1px solid',
          borderColor: 'divider',
          backdropFilter: 'blur(5px)',
          zIndex: 1000,
          boxShadow: isDarkTheme ? '0px -4px 12px rgba(0, 0, 0, 0.2)' : '0px -2px 4px rgba(0, 0, 0, 0.05)'
        }}>
          <Pagination 
            count={Math.ceil(totalPosts / limit)}
            page={page + 1}
            onChange={(e, newPage) => setPage(newPage - 1)}
            color="primary"
            size={isMobile ? "medium" : "large"}
            showFirstButton
            showLastButton
            sx={{
              '& .MuiPaginationItem-root': {
                borderRadius: isDarkTheme ? '4px' : '0px',
                color: 'text.primary',
                transition: 'all 0.2s ease',
              },
              '& .Mui-selected': {
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                '&:hover': {
                  bgcolor: 'primary.dark',
                },
              },
            }}
          />
        </Box>
      )}

      {/* Spacing for pagination */}
      <Box sx={{ mb: 8 }} />
    </Box>
  );
};

export default Forum;
